'use client';

import React, { useState, useMemo } from 'react';
import { ClipboardIcon, CheckCircleIcon, UsersIcon, RecipeCostIcon } from '../../Icons';

const QUICK_ADD_INGREDIENTS = [
    { name: 'Flour (AP)', packageCost: '5.00', packageWeight: '2268' },
    { name: 'Sugar', packageCost: '4.00', packageWeight: '1814' },
    { name: 'Butter', packageCost: '6.00', packageWeight: '454' },
    { name: 'Eggs (Dozen)', packageCost: '4.00', packageWeight: '600' },
    { name: 'Vanilla Extract', packageCost: '12.00', packageWeight: '118' },
    { name: 'Milk (Gallon)', packageCost: '4.00', packageWeight: '3785' },
];

interface Ingredient {
    id: number;
    name: string;
    packageCost: string;
    packageWeight: string;
    recipeWeight: string;
}

export const CostPerRecipeCalculatorClient: React.FC = () => {
    const [ingredients, setIngredients] = useState<Ingredient[]>([
        { id: 1, name: 'Flour', packageCost: '5.00', packageWeight: '2000', recipeWeight: '250' },
        { id: 2, name: 'Sugar', packageCost: '4.00', packageWeight: '1000', recipeWeight: '150' },
        { id: 3, name: 'Butter', packageCost: '6.00', packageWeight: '454', recipeWeight: '113' },
    ]);
    const [servings, setServings] = useState('12');
    const [copied, setCopied] = useState<string | null>(null);

    const handleIngredientChange = (id: number, field: keyof Omit<Ingredient, 'id'>, value: string) => {
        setIngredients(ingredients.map(ing => ing.id === id ? { ...ing, [field]: value } : ing));
    };
    const addIngredient = () => setIngredients([...ingredients, { id: Date.now(), name: '', packageCost: '', packageWeight: '', recipeWeight: '' }]);
    const addQuickIngredient = (item: typeof QUICK_ADD_INGREDIENTS[0]) => {
        setIngredients([...ingredients, { id: Date.now(), name: item.name, packageCost: item.packageCost, packageWeight: item.packageWeight, recipeWeight: '' }]);
    };
    const removeIngredient = (id: number) => ingredients.length > 1 && setIngredients(ingredients.filter(ing => ing.id !== id));
    
    const calculatedCosts = useMemo(() => ingredients.map(ing => {
        const pCost = parseFloat(ing.packageCost) || 0;
        const pWeight = parseFloat(ing.packageWeight) || 0;
        const rWeight = parseFloat(ing.recipeWeight) || 0;
        return pWeight > 0 ? (rWeight / pWeight) * pCost : 0;
    }), [ingredients]);

    const totalRecipeCost = useMemo(() => calculatedCosts.reduce((sum, cost) => sum + cost, 0), [calculatedCosts]);
    const costPerServing = useMemo(() => totalRecipeCost / (parseInt(servings) || 1), [totalRecipeCost, servings]);

    const handleCopy = (value: string, field: string) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        });
    };

    return (
        <div className="grid lg:grid-cols-3 gap-8 text-brand-text-body">
            <div className="lg:col-span-2 space-y-6">
                <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    {ingredients.map((ing, index) => (
                        <div key={ing.id} className="bg-white p-4 rounded-xl shadow-sm border border-brand-border">
                            <div className="flex gap-4 items-center mb-3">
                                <input type="text" placeholder="Ingredient Name" value={ing.name} onChange={e => handleIngredientChange(ing.id, 'name', e.target.value)} className="flex-grow px-3 py-2 bg-white border border-brand-border rounded-lg" />
                                <button onClick={() => removeIngredient(ing.id)} className="text-gray-400 hover:text-red-500 p-2">&times;</button>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <div><label className="block text-xs font-bold text-gray-400 mb-1">Price ($)</label><input type="number" value={ing.packageCost} onChange={e => handleIngredientChange(ing.id, 'packageCost', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1">Pkg (g)</label><input type="number" value={ing.packageWeight} onChange={e => handleIngredientChange(ing.id, 'packageWeight', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm" /></div>
                                <div><label className="block text-xs font-bold text-gray-400 mb-1">Used (g)</label><input type="number" value={ing.recipeWeight} onChange={e => handleIngredientChange(ing.id, 'recipeWeight', e.target.value)} className="w-full px-3 py-2 bg-gray-50 border border-brand-border rounded-lg text-sm" /></div>
                            </div>
                        </div>
                    ))}
                    <button onClick={addIngredient} className="w-full py-3 border-2 border-dashed border-brand-pink/30 text-brand-pink font-bold rounded-xl hover:bg-brand-pink/5 flex items-center justify-center gap-2">+ Add Ingredient</button>
                    <div className="flex flex-wrap gap-2 mt-4">
                        {QUICK_ADD_INGREDIENTS.map((item, idx) => (
                            <button key={idx} onClick={() => addQuickIngredient(item)} className="px-3 py-1 bg-white border border-brand-border rounded-full text-xs hover:text-brand-pink transition-colors">+ {item.name}</button>
                        ))}
                    </div>
                </div>
            </div>
            <div className="lg:col-span-1">
                <div className="bg-brand-pink-light p-6 rounded-2xl border border-brand-pink/20 text-center sticky top-6">
                    <RecipeCostIcon className="w-12 h-12 mx-auto text-brand-pink mb-3" />
                    <h3 className="font-serif text-xl font-bold text-brand-text-title mb-1">Total Cost</h3>
                    <p className="font-bold text-5xl text-brand-pink mb-4">${totalRecipeCost.toFixed(2)}</p>
                    <button onClick={() => handleCopy(totalRecipeCost.toFixed(2), 'total')} className="text-xs text-brand-pink underline mb-6 block w-full">{copied === 'total' ? 'Copied!' : 'Copy Total'}</button>
                    <div className="pt-6 border-t border-brand-pink/10">
                        <label className="block text-sm font-bold text-brand-text-body mb-2">Servings</label>
                        <input type="number" value={servings} onChange={e => setServings(e.target.value)} className="w-20 px-3 py-2 bg-white border border-brand-pink/30 rounded-lg text-center font-bold" />
                        <div className="mt-6 bg-white/60 p-4 rounded-xl border border-brand-pink/10">
                            <h4 className="font-bold text-brand-text-title">Cost Per Serving</h4>
                            <p className="font-bold text-3xl text-brand-text-title">${costPerServing.toFixed(2)}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};
